/* Post-ictal Voice Memo — MediaRecorder capture with animated wave bars,
   plus Bluetooth media-key stealth trigger (headphone remote click starts).*/
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Microphone, Stop, Trash, Play } from "@phosphor-icons/react";
import health from "./healthApi";
import { getHealthToken } from "./healthApi";
import api from "@/lib/api";

export default function VoiceMemos() {
  const [memos, setMemos] = useState([]);
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [uploading, setUploading] = useState(false);
  const rec = useRef(null);
  const chunks = useRef([]);
  const stream = useRef(null);
  const tick = useRef(null);

  const load = () => health.get("/voice-memos").then((r) => setMemos(r.data));
  useEffect(() => { load(); }, []);

  const stop = () => {
    if (rec.current && rec.current.state !== "inactive") rec.current.stop();
    stream.current?.getTracks().forEach((t) => t.stop());
    clearInterval(tick.current);
    setRecording(false);
  };

  const start = async () => {
    try {
      stream.current = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      rec.current = new MediaRecorder(stream.current, mime ? { mimeType: mime } : undefined);
      chunks.current = [];
      rec.current.ondataavailable = (e) => { if (e.data.size > 0) chunks.current.push(e.data); };
      rec.current.onstop = async () => {
        const blob = new Blob(chunks.current, { type: rec.current.mimeType || "audio/webm" });
        setUploading(true);
        const fd = new FormData();
        fd.append("file", blob, `memo-${Date.now()}.webm`);
        try {
          await api.post(`/health/voice-memo?duration_sec=${seconds}`, fd, {
            headers: { "X-Health-Token": getHealthToken() },
          });
          toast.success("Voice memo saved");
          setSeconds(0);
          load();
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Upload failed");
        } finally {
          setUploading(false);
        }
      };
      rec.current.start();
      setRecording(true);
      setSeconds(0);
      tick.current = setInterval(() => setSeconds((s) => s + 1), 1000);
      toast.info("Recording — speak clearly");
    } catch (e) {
      toast.error("Microphone permission denied");
    }
  };

  // Hardware / Bluetooth media button trigger
  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    try {
      navigator.mediaSession.setActionHandler("play", () => { if (!recording) start(); });
      navigator.mediaSession.setActionHandler("pause", () => { if (recording) stop(); });
      navigator.mediaSession.setActionHandler("stop", stop);
    } catch { /* not supported */ }
    return () => {
      try {
        navigator.mediaSession.setActionHandler("play", null);
        navigator.mediaSession.setActionHandler("pause", null);
        navigator.mediaSession.setActionHandler("stop", null);
      } catch { /* noop */ }
    };
     
  }, [recording]);

  const del = async (id) => {
    if (!confirm("Delete this memo?")) return;
    await health.delete(`/voice-memo/${id}`);
    load();
  };

  return (
    <div data-testid="nl-voice-tab">
      <div className="mb-4">
        <div className="font-label text-[10px] tracking-widest" style={{ color: "var(--nl-accent)" }}>POST-ICTAL VOICE MEMO</div>
        <div className="font-display text-2xl font-black" style={{ color: "var(--nl-text)" }}>Record & keep</div>
      </div>

      <div className="rounded-2xl p-6 text-center mb-4" style={{ background: "var(--nl-panel)", border: `1px solid ${recording ? "var(--nl-danger)" : "var(--nl-line)"}` }}>
        {recording ? (
          <>
            <div className="h-16 flex items-end justify-center">
              {[0,1,2,3,4,5].map((i) => <span key={i} className="nl-wave-bar" style={{ height: 40 }} />)}
            </div>
            <div className="font-display tabular-nums text-2xl mt-2" style={{ color: "var(--nl-accent)" }}>{seconds}s</div>
            <button
              data-testid="nl-voice-stop"
              onClick={stop}
              className="mt-4 h-12 px-8 rounded-xl font-label text-xs tracking-widest inline-flex items-center gap-2"
              style={{ background: "var(--nl-accent)", color: "#001018" }}
            >
              <Stop size={16} weight="fill" /> STOP & SAVE
            </button>
          </>
        ) : (
          <>
            <button
              data-testid="nl-voice-start"
              onClick={start}
              disabled={uploading}
              className="h-16 w-16 rounded-full nl-ice-pulse mx-auto flex items-center justify-center disabled:opacity-40"
              style={{ background: "var(--nl-danger)" }}
            >
              <Microphone size={26} weight="fill" color="#fff" />
            </button>
            <div className="text-xs mt-3" style={{ color: "var(--nl-muted)" }}>
              {uploading ? "Uploading…" : "Tap to record · press headphone play/pause to trigger hands-free"}
            </div>
          </>
        )}
      </div>

      <div className="space-y-2">
        {memos.length === 0 && (
          <div className="text-xs text-center py-4" style={{ color: "var(--nl-muted)" }}>No memos yet.</div>
        )}
        {memos.map((m) => (
          <div key={m.id} className="rounded-lg p-3 flex items-center gap-3"
               style={{ background: "var(--nl-panel)", border: "1px solid var(--nl-line)" }}>
            <audio controls src={m.url ? `${process.env.REACT_APP_BACKEND_URL}${m.url}` : ""} className="flex-1 h-10" />
            <div className="text-[11px] font-mono text-right shrink-0" style={{ color: "var(--nl-muted)" }}>
              <div>{m.duration_sec}s</div>
              <div>{m.created_at?.slice(0, 16).replace("T"," ")}</div>
            </div>
            <button onClick={() => del(m.id)} style={{ color: "var(--nl-danger)" }}><Trash size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
